import { Controller } from '@hotwired/stimulus';
import * as Turbo from "@hotwired/turbo"

export class ModalController extends Controller {
    declare readonly panelTarget: HTMLElement
    declare readonly backdropTarget: HTMLElement
    static targets = ["panel", "backdrop"]

    declare readonly typeValue: string
    declare readonly confirmUrlValue: string
    declare readonly confirmEventValue: string
    declare readonly rejectUrlValue: string
    declare readonly rejectEventValue: string
    declare readonly persistentValue: boolean
    declare readonly hiddenValue: boolean
    declare readonly reusableValue: boolean
    static values = {
        type: String,
        confirmUrl: String,
        confirmEvent: String,
        rejectUrl: String,
        rejectEvent: String,
        persistent: Boolean,
        hidden: Boolean,
        reusable: Boolean
    }

    connect(): void {
        console.log('Modal connected, persistent:', this.persistentValue, 'type:', typeof this.persistentValue, 'hidden:', this.hiddenValue)
        // If modal is hidden, don't auto-show
        if (!this.hiddenValue) {
            this.show()
        }
    }

    show(): void {
        document.body.classList.add("overflow-hidden")
        this.element.classList.remove("hidden")

        // Short timeout to allow DOM to render before animating
        setTimeout(() => {
            this.open()
        }, 10)
    }

    disconnect(): void {
        document.body.classList.remove("overflow-hidden")
    }

    open(): void {
        // Apply open state classes
        this.element.classList.remove("opacity-0")
        this.element.classList.add("opacity-100")

        this.backdropTarget.classList.remove("opacity-0")
        this.backdropTarget.classList.add("opacity-100")

        this.panelTarget.classList.remove("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95")
        this.panelTarget.classList.add("opacity-100", "translate-y-0", "sm:scale-100")
    }

    close(): void {
        // Apply close state classes
        this.element.classList.remove("opacity-100", "ease-out", "duration-300")
        this.element.classList.add("opacity-0", "ease-in", "duration-200")

        this.backdropTarget.classList.remove("opacity-100")
        this.backdropTarget.classList.add("opacity-0")

        this.panelTarget.classList.remove("opacity-100", "translate-y-0", "sm:scale-100", "ease-out", "duration-300")
        this.panelTarget.classList.add("opacity-0", "translate-y-4", "sm:translate-y-0", "sm:scale-95", "ease-in", "duration-200")

        // Remove element after animation completes, or hide if reusable
        setTimeout(() => {
            if (this.reusableValue) {
                // If modal is reusable, just hide it instead of removing
                this.element.classList.add("hidden")
                document.body.classList.remove("overflow-hidden")
            } else {
                // If modal is not reusable, remove it from DOM
                this.element.remove()
            }
        }, 10)
    }

    backdropClicked(): void {
        console.log('Backdrop clicked, persistent value:', this.persistentValue, 'type:', typeof this.persistentValue)
        if (!this.persistentValue) {
            this.close()
        }
    }

    confirm(): void {
        if (this.confirmUrlValue) {
            Turbo.visit(this.confirmUrlValue)
        } else if (this.confirmEventValue) {
            this.dispatchCustomEvent(this.confirmEventValue)
        } else {
            this.close()
        }
    }

    reject(): void {
        if (this.rejectUrlValue) {
            Turbo.visit(this.rejectUrlValue)
        } else if (this.rejectEventValue) {
            this.dispatchCustomEvent(this.rejectEventValue)
        } else {
            this.close()
        }
    }

    private dispatchCustomEvent(eventName: string): void {
        const event = new CustomEvent(eventName, {
            bubbles: true,
            detail: {
                type: this.typeValue,
                modal: this.element
            }
        })
        this.element.dispatchEvent(event)
        this.close()
    }
}